/**
 * Xsolla configuration constants
 *
 * Product catalog (plan IDs, prices, tiers) lives here in code.
 * Runtime settings (merchant ID, project ID, sandbox mode, enabled)
 * are read from file-backed config/environment via the config service on the backend,
 * or passed to the frontend via site config.
 *
 * ============================================================
 * SANDBOX TEST CARDS
 * ============================================================
 * Use these test card numbers in sandbox mode:
 *
 * | Card Number          | Result              |
 * |----------------------|---------------------|
 * | 4111 1111 1111 1111  | Successful payment  |
 * | 4000 0000 0000 0002  | Declined            |
 * | 4000 0000 0000 0069  | Expired card        |
 * | 4000 0000 0000 0127  | Incorrect CVV       |
 *
 * - Expiry: Any future date (e.g., 12/30)
 * - CVV: Any 3 digits (e.g., 123)
 * - Cardholder: Any name
 *
 * View sandbox transactions in Xsolla Publisher Account:
 * https://publisher.xsolla.com → Project → Pay Station → Testing
 * ============================================================
 */

import {
  TPurchaseBillingPeriod,
  TPurchaseFulfillmentKind,
  TPurchaseKind
} from './purchases';
import { XSOLLA_TIERS, TXsollaTier } from './xsolla';

/**
 * Subscription tier names
 */
export type TXsollaSubscriptionTier = 'revered' | 'exalted' | 'illustrious';

type TXsollaCatalogProductBase = {
  purchaseKind: TPurchaseKind;
  fulfillmentKind: TPurchaseFulfillmentKind;
  productCode: string;
};

/**
 * Subscription product configuration
 */
export type TXsollaSubscriptionProduct = TXsollaCatalogProductBase & {
  purchaseKind: 'subscription';
  fulfillmentKind: 'subscription';
  tier: TXsollaSubscriptionTier;
  planId: string;
  planIdAnnual: string;
  monthlyPriceUsd: number;
  annualPriceUsd: number;
};

export type TXsollaInventoryProduct = TXsollaCatalogProductBase & {
  purchaseKind: 'inventory';
  fulfillmentKind: 'inventory';
  sku: string;
  entitlementKey: string;
  priceUsd?: number;
};

export type TXsollaCatalogProduct = TXsollaSubscriptionProduct | TXsollaInventoryProduct;

export type TXsollaResolvedCatalogItem = {
  product: TXsollaCatalogProduct;
  billingPeriod?: TPurchaseBillingPeriod;
};

/**
 * Valid subscription tiers (derived from config)
 */
export const VALID_SUBSCRIPTION_TIERS: TXsollaSubscriptionTier[] = ['revered', 'exalted', 'illustrious'];

/**
 * Tier name to XSOLLA_TIERS key mapping
 */
export const TIER_NAME_TO_KEY: Record<TXsollaSubscriptionTier, keyof typeof XSOLLA_TIERS> = {
  revered: 'REVERED',
  exalted: 'EXALTED',
  illustrious: 'ILLUSTRIOUS'
};

/**
 * Build subscription products from XSOLLA_TIERS with plan IDs
 */
function buildSubscriptionProducts(planIds: Record<TXsollaSubscriptionTier, { monthly: string; annual: string }>): Record<TXsollaSubscriptionTier, TXsollaSubscriptionProduct> {
  const result = {} as Record<TXsollaSubscriptionTier, TXsollaSubscriptionProduct>;

  for (const tier of VALID_SUBSCRIPTION_TIERS) {
    const tierConfig = XSOLLA_TIERS[TIER_NAME_TO_KEY[tier]];
    result[tier] = {
      purchaseKind: 'subscription',
      fulfillmentKind: 'subscription',
      tier,
      planId: planIds[tier].monthly,
      planIdAnnual: planIds[tier].annual,
      productCode: tierConfig.productCode,
      monthlyPriceUsd: tierConfig.monthlyPriceUsd,
      annualPriceUsd: tierConfig.annualPriceUsd
    };
  }

  return result;
}

/**
 * Static configuration that doesn't change between environments
 */
export const XSOLLA_CONFIG = {
  /**
   * API endpoints
   */
  api: {
    baseUrl: 'https://api.xsolla.com',
    subscriberAccountBaseUrl: 'https://subscriptions.xsolla.com/api/v1',
    subscriptionsPath: '/merchant/v2/merchants/{merchantId}/subscriptions',
    tokenPath: '/merchant/v2/merchants/{merchantId}/token'
  },

  /**
   * Webhook configuration
   */
  webhook: {
    path: '/xsolla/webhook'
  },

  /**
   * Payment UI configuration
   */
  payStation: {
    lightbox: {
      width: '740px',
      height: '760px',
      spinner: 'round' as const,
      spinnerColor: '#ff6b00' // RaiderIO orange
    }
  },

  /**
   * Product catalog (plan IDs + prices from XSOLLA_TIERS)
   */
  products: {
    subscriptions: buildSubscriptionProducts({
      revered: { monthly: 'MbYJcs4E', annual: 'v0AxdS3l' },
      exalted: { monthly: 'iZJxKDZ1', annual: 'svi3IMv1' },
      illustrious: { monthly: '4MT4rxhU', annual: '0PZkZI4S' }
    }),
    inventory: {} as Record<string, TXsollaInventoryProduct>
  }
} as const;

/**
 * Get plan ID by tier name and billing period
 */
export function getXsollaPlanId(tier: TXsollaSubscriptionTier, isAnnual: boolean): string {
  const product = XSOLLA_CONFIG.products.subscriptions[tier];
  return isAnnual ? product.planIdAnnual : product.planId;
}

/**
 * Build a map of plan ID to tier key for reverse lookups
 */
export function buildPlanIdToTierMap(): Record<string, TXsollaSubscriptionTier> {
  const map: Record<string, TXsollaSubscriptionTier> = {};
  const { subscriptions } = XSOLLA_CONFIG.products;

  for (const [tier, product] of Object.entries(subscriptions)) {
    if (product.planId) {
      map[product.planId] = tier as TXsollaSubscriptionTier;
    }
    if (product.planIdAnnual) {
      map[product.planIdAnnual] = tier as TXsollaSubscriptionTier;
    }
  }

  return map;
}

/**
 * Build a map of plan ID to catalog item metadata for reverse lookups
 */
export function buildPlanIdToCatalogItemMap(): Record<string, TXsollaResolvedCatalogItem> {
  const map: Record<string, TXsollaResolvedCatalogItem> = {};
  const { subscriptions } = XSOLLA_CONFIG.products;

  for (const product of Object.values(subscriptions)) {
    if (product.planId) {
      map[product.planId] = {
        product,
        billingPeriod: 'monthly'
      };
    }
    if (product.planIdAnnual) {
      map[product.planIdAnnual] = {
        product,
        billingPeriod: 'annual'
      };
    }
  }

  return map;
}

/**
 * Build a map of Xsolla external product IDs to catalog item metadata.
 * Includes subscription plans and one-time inventory SKUs.
 */
export function buildExternalProductIdToCatalogItemMap(): Record<string, TXsollaResolvedCatalogItem> {
  const map = buildPlanIdToCatalogItemMap();
  const { inventory } = XSOLLA_CONFIG.products;

  for (const product of Object.values(inventory)) {
    if (product.sku) {
      map[product.sku] = {
        product,
        billingPeriod: 'one_time'
      };
    }
  }

  return map;
}

/**
 * Cached plan ID to tier map - built once on first use
 */
let cachedPlanIdToTierMap: Record<string, TXsollaSubscriptionTier> | null = null;
let cachedPlanIdToCatalogItemMap: Record<string, TXsollaResolvedCatalogItem> | null = null;
let cachedExternalProductIdToCatalogItemMap: Record<string, TXsollaResolvedCatalogItem> | null = null;

/**
 * Get cached plan ID to tier map (lazy-initialized)
 */
function getPlanIdToTierMap(): Record<string, TXsollaSubscriptionTier> {
  if (!cachedPlanIdToTierMap) {
    cachedPlanIdToTierMap = buildPlanIdToTierMap();
  }
  return cachedPlanIdToTierMap;
}

function getPlanIdToCatalogItemMap(): Record<string, TXsollaResolvedCatalogItem> {
  if (!cachedPlanIdToCatalogItemMap) {
    cachedPlanIdToCatalogItemMap = buildPlanIdToCatalogItemMap();
  }
  return cachedPlanIdToCatalogItemMap;
}

function getExternalProductIdToCatalogItemMap(): Record<string, TXsollaResolvedCatalogItem> {
  if (!cachedExternalProductIdToCatalogItemMap) {
    cachedExternalProductIdToCatalogItemMap = buildExternalProductIdToCatalogItemMap();
  }
  return cachedExternalProductIdToCatalogItemMap;
}

/**
 * Get tier name by plan ID
 */
export function getTierNameByPlanId(planId: string): TXsollaSubscriptionTier | null {
  return getPlanIdToTierMap()[planId] || null;
}

/**
 * Resolve a catalog item by external Xsolla plan ID
 */
export function getCatalogItemByPlanId(planId: string): TXsollaResolvedCatalogItem | null {
  return getPlanIdToCatalogItemMap()[planId] || null;
}

/**
 * Resolve a catalog item by external Xsolla product ID.
 * Supports subscription plan IDs and inventory SKUs.
 */
export function getCatalogItemByExternalProductId(externalProductId: string): TXsollaResolvedCatalogItem | null {
  return getExternalProductIdToCatalogItemMap()[externalProductId] || null;
}

/**
 * Resolve a subscription product by external Xsolla plan ID
 */
export function getSubscriptionProductByPlanId(planId: string): TXsollaSubscriptionProduct | null {
  const item = getCatalogItemByPlanId(planId);
  return item?.product.purchaseKind === 'subscription' ? item.product : null;
}

/**
 * Resolve an inventory product by external Xsolla SKU.
 */
export function getInventoryProductBySku(sku: string): TXsollaInventoryProduct | null {
  const item = getCatalogItemByExternalProductId(sku);
  return item?.product.purchaseKind === 'inventory' ? item.product : null;
}

/**
 * Get product code by plan ID
 */
export function getProductCodeByPlanId(planId: string): string | null {
  return getCatalogItemByPlanId(planId)?.product.productCode || null;
}

/**
 * Get tier config by plan ID
 */
export function getTierByPlanId(planId: string): TXsollaTier | null {
  const product = getSubscriptionProductByPlanId(planId);
  return product ? XSOLLA_TIERS[TIER_NAME_TO_KEY[product.tier]] ?? null : null;
}
